import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Container, Row, Col, Button } from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import TableFilter from "../../../components/TableFilter";
import ConfirmationAlert from "../../../components/ConfirmationAlert";
import ProcessDialog from "../dialogs/ProcessDialog";

const fetchData = async ({ queryKey }) => {
  const [_, token, deviceId] = queryKey;
  const response = await fetch(
    `${MOBY_API_URL}/api/devicemanager/v1/devices/${deviceId}/mobileusers?txid=${token.sessionId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token.token,
      },
    }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const deleteUser = async ({ token, deviceId, emailAddress }) => {
  const response = await fetch(
    `${MOBY_API_URL}/api/devicemanager/v1/devices/${deviceId}/removeuser?emailAddress=${emailAddress}&txid=${token.sessionId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "sentinel-token": token.token,
      },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to delete user");
  }
  return response.json();
};

const UsersListByPanel = ({ panel, token, setSelectedPanel }) => {
  const queryClient = useQueryClient();

  const {
    data: initialData = [],
    isLoading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ["users", token, panel?.cloudId],
    queryFn: fetchData,
    enabled: !!panel?.cloudId,
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialog, setDialog] = useState({
    show: false,
    refresh: false
  });
  const [dialogUsers, setDialogUsers] = useState([]);
  const [updatedUsers, setUpdatedUsers] = useState([]);
  
  const updateDialog = (show, refresh) => {
    setDialog((prev) => ({
      ...prev,
      show,
      refresh
    }));
  }

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onMutate: async ({ emailAddress }) => {
      setUpdatedUsers((prev) => [...prev, { emailAddress, status: "pending" }]);
    },
    onSuccess: (_, { emailAddress }) => {
      setUpdatedUsers((prev) =>
        prev.map((user) =>
          user.emailAddress === emailAddress
            ? { ...user, status: "success" }
            : user
        )
      );
    },
    onError: (_, { emailAddress }) => {
      setUpdatedUsers((prev) =>
        prev.map((user) =>
          user.emailAddress === emailAddress
            ? { ...user, status: "error" }
            : user
        )
      );
    },
    onSettled: () => {}
  });

  const confirmDelete = () => {
    const selectedEmails = Object.keys(rowSelection);
    const selectedUsersList = initialData?.data?.filter((row) =>
      selectedEmails.includes(row.email)
    );

    setDialogUsers((prevDialogUsers) => [
      ...prevDialogUsers,
      ...selectedUsersList.map((user) => ({
        ...user,
        emailAddress: user?.email,
        cloudId: panel.cloudId,
        status: "pending",
      })),
    ]);
    updateDialog(true, true);

    selectedUsersList.forEach((user) => {
      deleteUserMutation.mutate({
        token,
        deviceId: panel.deviceId,
        emailAddress: user.email,
      });
    });

    setRowSelection({});
    setShowConfirm(false);
  };

  const deleteAgain = (email) => {
    deleteUserMutation.mutate({
      token,
      deviceId: panel.deviceId,
      emailAddress: email,
    });
  };

  const cancelDelete = () => {
    setShowConfirm(false);
  };

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "firstName",
        header: "First Name",
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
      },
    ],
    []
  );

  const table = useReactTable({
    data: initialData?.data || [],
    columns,
    state: {
      globalFilter,
      rowSelection,
    },
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "auto",
    enableRowSelection: true,
    getRowId: (row) => row.email,
  });

  const handleSelectAll = () => {
    const allEmails = table.getRowModel().rows.map((row) => row.id);
    const isAllSelected = allEmails.every((email) => rowSelection[email]);
    if (isAllSelected) {
      setRowSelection({});
    } else {
      const newSelection = {};
      allEmails.forEach((email) => {
        newSelection[email] = true;
      });
      setRowSelection(newSelection);
    }
  };

  const isAllSelected = useMemo(() => {
    const allEmails = table.getRowModel().rows.map((row) => row.id);
    return (
      allEmails.length > 0 && allEmails.every((email) => rowSelection[email])
    );
  }, [rowSelection, table]);
 
  const handleOpenDialog = () => updateDialog(true, false);
  const handleCloseDialog = () => {
    if (dialog.refresh) {
      queryClient.invalidateQueries(["users", token, panel?.deviceId]);
      setSelectedPanel(null);
    }
    updateDialog(false, false);
  }
  if (isLoading || isFetching) {
    return <p>Loading...</p>;
  }

  if (isError) {
    return <p>Couldn't get Users</p>;
  }

  return (
    <Container>
      <Row className="mt-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Button
              variant="primary"
              onClick={handleSelectAll}
              className="me-2"
              disabled={initialData?.data?.length === 0}
            >
              {isAllSelected ? "Deselect All" : "Select All"}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={Object.keys(rowSelection).length === 0}
            >
              Remove Selected Users
            </Button>
            <Button
              variant="primary"
              onClick={handleOpenDialog}
              className="ms-auto"
              disabled={Object.keys(rowSelection).length === 0}
            >
              Show Progress
            </Button>
          </div>

          <div className="d-flex align-items-center w-100">
            <div className="flex-grow-1">
              <TableFilter
                filterValue={globalFilter}
                setFilter={setGlobalFilter}
              />
            </div>
          </div>

          <Table bordered hover variant="info" className="mt-2 fixed-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className={header.id == "email" ? "email-col" : ""}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => row.toggleSelected()}
                  style={{
                    backgroundColor: row.getIsSelected()
                      ? "lightblue"
                      : "white",
                    cursor: "pointer",
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{ backgroundColor: "inherit", cursor: "pointer" }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>

          <ConfirmationAlert
            text="Are you sure you want to delete the selected users? This action cannot be undone."
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
            show={showConfirm}
            onHide={() => setShowConfirm(false)}
          />

          <ProcessDialog
            show={dialog.show}
            onHide={handleCloseDialog}
            infoMessage="Processing selected users and panels."
            users={dialogUsers}
            updatedUsers={updatedUsers}
            deleteAgain={deleteAgain}
            searchBy="panel"
          />
        </Col>
      </Row>
    </Container>
  );
};

export default UsersListByPanel;
